import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { DashboardService } from '../../core/services/dashboard.service';
import { EquipmentService } from '../../core/services/equipment.service';
import { ExportService } from '../../core/services/export.service';
import { InventoryService } from '../../core/services/inventory.service';
import { MaintenanceService } from '../../core/services/maintenance.service';
import { environment } from '../../environments/environment';
import { CheckoutRecord } from '../../core/models/equipment.models';
import { StockSnapshot } from '../../core/models/inventory.models';
import {
  currentTimestamp,
  formatCurrency,
  formatDate,
} from '../../core/utils/format.util';

function todayIso(): string {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

function dateKey(value: string | undefined): string {
  return (value ?? '').slice(0, 10);
}

@Component({
  selector: 'app-dashboard',
  imports: [RouterLink],
  standalone: true,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
})
export class DashboardComponent implements OnInit, OnDestroy {
  private readonly dashboard = inject(DashboardService);
  private readonly inventory = inject(InventoryService);
  private readonly equipment = inject(EquipmentService);
  private readonly maintenance = inject(MaintenanceService);
  private readonly exporter = inject(ExportService);

  readonly timestamp = currentTimestamp;
  readonly formatCurrency = formatCurrency;
  readonly formatDate = formatDate;
  readonly useLiveApi = !environment.useMockData;

  readonly isLoading = computed(
    () =>
      this.useLiveApi &&
      this.dashboard.isLoading() &&
      this.inventory.allStockSnapshots().length === 0 &&
      this.inventory.allItems().length === 0,
  );

  readonly showToast = signal(false);
  readonly toastMessage = signal('');
  readonly toastType = signal<'success' | 'error' | 'info'>('error');
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  readonly itemStats = computed(() => this.inventory.itemStats());

  readonly stockSnapshots = computed(() => this.inventory.allStockSnapshots());

  readonly lowStockAlerts = computed(() =>
    this.stockSnapshots()
      .filter((row) => row.stockStatus === 'low')
      .slice(0, 5),
  );

  readonly lowStockCount = computed(
    () =>
      this.stockSnapshots().filter((row) => row.stockStatus === 'low').length,
  );

  readonly outOfStockCount = computed(
    () =>
      this.stockSnapshots().filter(
        (row) => row.quantity <= 0 || row.stockStatus === 'critical',
      ).length,
  );

  readonly inventoryValue = computed(() =>
    this.stockSnapshots().reduce(
      (sum, row) => sum + row.quantity * (row.unitValue || 0),
      0,
    ),
  );

  readonly goodsReceivedToday = computed(() => {
    const today = todayIso();
    return this.inventory
      .allGoodsReceived()
      .filter(
        (row) => row.status === 'posted' && dateKey(row.receivedDate) === today,
      ).length;
  });

  readonly issuesToday = computed(() => {
    const today = todayIso();
    return this.inventory
      .issuedOutStock()
      .filter((row) => dateKey(row.issueDate) === today).length;
  });

  readonly overdueReturns = computed(() =>
    this.equipment.getOverdueCheckouts().slice(0, 5),
  );
  readonly overdueCount = computed(() => this.equipment.stats().overdue);
  readonly equipmentStats = computed(() => this.equipment.stats());
  readonly totalAssets = computed(() => this.equipment.allAssets().length);

  readonly reservedCount = computed(
    () =>
      this.equipment
        .allReservations()
        .filter((row) => row.status === 'pending' || row.status === 'confirmed')
        .length,
  );

  readonly inRepairCount = computed(
    () =>
      this.equipment.allAssets().filter((asset) => asset.status === 'in-repair')
        .length,
  );

  readonly damagedCount = computed(
    () => this.equipment.getDamagedRecords().length,
  );

  readonly openMaintenanceCount = computed(() => {
    const stats = this.maintenance.jobStats();
    return stats.pending + stats.inProgress;
  });

  readonly recentIssues = computed(() =>
    this.inventory.issuedOutStock().slice(0, 5),
  );

  readonly recentCheckouts = computed(() =>
    this.equipment
      .allCheckouts()
      .filter((row) => row.status === 'active' || row.status === 'overdue')
      .slice(0, 5),
  );

  readonly recentFeed = computed(() => {
    const issues = this.recentIssues().map((issue) => ({
      kind: 'issue' as const,
      id: `issue-${issue.id}`,
      title: issue.itemName || issue.issueNumber,
      detail: issue.issuedTo,
      date: issue.issueDate,
      route: '/issued-stock',
    }));
    const checkouts = this.recentCheckouts().map((row) => ({
      kind: 'checkout' as const,
      id: `checkout-${row.id}`,
      title: row.equipment,
      detail: row.customer,
      date: row.checkoutDate || row.dueDate,
      route: '/check-in',
    }));
    return [...issues, ...checkouts]
      .sort((a, b) => dateKey(b.date).localeCompare(dateKey(a.date)))
      .slice(0, 8);
  });

  ngOnInit(): void {
    this.refresh();
  }

  ngOnDestroy(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
  }

  refresh(): void {
    if (!environment.useMockData) {
      this.dashboard.load();
      this.inventory.refreshFromApi();
      this.equipment.refreshFromApi();
      this.maintenance.refreshFromApi();
    }
  }

  closeToast(): void {
    this.showToast.set(false);
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
  }

  snapshotLabel(row: StockSnapshot): string {
    return `${row.itemName || row.itemCode} · ${row.location || row.branch || 'Stock'}`;
  }

  checkoutLabel(row: CheckoutRecord): string {
    return `${row.equipment} · ${row.customer}`;
  }

  exportData(): void {
    const rows = [
      ...this.recentIssues().map((issue) => ({
        section: 'Recent issues',
        reference: issue.issueNumber,
        name: issue.issuedTo,
        date: issue.issueDate,
        value: issue.items,
      })),
      ...this.overdueReturns().map((checkout) => ({
        section: 'Overdue returns',
        reference: checkout.checkoutNumber,
        name: checkout.equipment,
        date: checkout.dueDate,
        value: checkout.customer,
      })),
      ...this.lowStockAlerts().map((item) => ({
        section: 'Low stock',
        reference: item.itemCode,
        name: item.itemName,
        date: '',
        value: `${item.quantity} at ${item.location}`,
      })),
    ];

    if (!rows.length) {
      this.openToast('Nothing to export yet.', 'info');
      return;
    }

    this.exporter.downloadCsv(
      `inventory-dashboard-${new Date().toISOString().slice(0, 10)}`,
      rows,
    );
  }

  private openToast(message: string, type: 'success' | 'error' | 'info'): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.toastMessage.set(message);
    this.toastType.set(type);
    this.showToast.set(true);
    this.toastTimer = setTimeout(() => this.closeToast(), 5000);
  }
}
